import React from 'react';
import { Link, Project, ProjectType } from '../../StateContext';
import { secondsToHours, totalProjectHours, totalProjectBaseline, hoursForType, hourMultipliedByWeight } from '../Links/weekView';
import './hourOverviewPage.css';
import { useLanguage } from '../../Services/LanguageContext';

interface IProps {
    link?: Link[];
    project?: Project;
}

const HourOverviewPage = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
    const { t } = useLanguage();

    if (!props.project || !props.link || props.link.length === 0) {
        return (
            <div ref={ref} className='print-page'>
                <div className='print-header'>
                    <h1 className='print-title'>{t('noDataAvailable')}</h1>
                    <div className='print-project-code'>{t('ensureProjectData')}</div>
                </div>
            </div>
        );
    }

    const projectTypes  = props.project.types ?? [];
    const hasTypes      = projectTypes.length > 0;
    const dhzSeconds    = props.project.dhzSeconds ?? 0;
    const meerwerkHours = secondsToHours(props.project.meerwerkSeconds ?? 0);
    const dhzHours      = secondsToHours(dhzSeconds);
    const workedHours   = totalProjectHours(props.link);
    const baseline      = totalProjectBaseline(props.link, dhzSeconds);
    const estimatedHours = hasTypes
        ? projectTypes.reduce((sum, pt) => sum + secondsToHours(pt.calculatedTimeInSeconds), 0)
        : secondsToHours(props.project.estimatedTimeInSeconds);
    const remaining = estimatedHours - baseline;

    const getWeekDataForWeekAndType = (link: Link, wn: number, typeId?: number) =>
        link.weekData.find(w => w.weekNumber === wn && (typeId === undefined || w.projectTypeId === typeId)) || null;

    const getWeekNumbers = (links: Link[], typeId?: number): number[] => {
        const s = new Set<number>();
        links.forEach(l =>
            l.weekData.forEach(w => {
                if (typeId === undefined || w.projectTypeId === typeId) s.add(w.weekNumber);
            })
        );
        return Array.from(s).sort((a, b) => a - b);
    };

    const getTotalHoursForWeekAndType = (wn: number, typeId?: number): number =>
        (props.link ?? []).reduce((t, l) => {
            const w = l.weekData.find(wd => wd.weekNumber === wn && (typeId === undefined || wd.projectTypeId === typeId));
            return t + (w ? hourMultipliedByWeight(secondsToHours(w.secondsWorked), w.weight) : 0);
        }, 0);

    const getMechanicWeightedTotalForType = (link: Link, typeId?: number): number =>
        link.weekData
            .filter(w => typeId === undefined || w.projectTypeId === typeId)
            .reduce((t, w) => t + hourMultipliedByWeight(secondsToHours(w.secondsWorked), w.weight), 0);

    const renderTypeTable = (pt: ProjectType) => {
        const typeLinks  = props.link.filter(l => l.weekData.some(w => w.projectTypeId === pt.id));
        const weekNums   = getWeekNumbers(props.link, pt.id);
        const typeActual = hoursForType(props.link, pt.id);
        const typeEst    = secondsToHours(pt.calculatedTimeInSeconds);
        const typeResult = typeEst - typeActual;
        const colSpan    = weekNums.length + 2;

        return (
            <table key={pt.id} className='print-table' style={{ marginBottom: '10px' }}>
                <thead>
                    <tr>
                        <th
                            className='print-th'
                            colSpan={colSpan}
                            style={{ background: '#e8e8e8', textAlign: 'left', paddingLeft: '8px', fontSize: '11px', letterSpacing: '0.05em' }}
                        >
                            {pt.name.toUpperCase()}
                            <span style={{ float: 'right', fontWeight: 'normal', fontSize: '10px' }}>
                                {t('hoursEstimated')}: {typeEst.toFixed(2)}u &nbsp;|&nbsp;
                                {t('hoursWorked')}: {typeActual.toFixed(2)}u &nbsp;|&nbsp;
                                {t('result')}: <span className={typeResult >= 0 ? 'positive' : 'negative'}>{typeResult.toFixed(2)}u</span>
                            </span>
                        </th>
                    </tr>
                    <tr>
                        <th className='print-th print-mechanic-col' style={{ textAlign: 'left', paddingLeft: '8px' }}>{t('mechanic')}</th>
                        {weekNums.map(wn => (
                            <th key={wn} className='print-th print-week-header-col'>{t('week')} {wn}</th>
                        ))}
                        <th className='print-th print-total-col'>{t('total')}</th>
                    </tr>
                </thead>
                <tbody>
                    {typeLinks.map((link, i) => (
                        <tr key={i}>
                            <td className='print-td print-mechanic-name-col'>
                                {(link as any).mechanic?.name || `${t('mechanic')} ${link.mechanicId}`}
                            </td>
                            {weekNums.map(wn => {
                                const wd = getWeekDataForWeekAndType(link, wn, pt.id);
                                const hours = wd ? hourMultipliedByWeight(secondsToHours(wd.secondsWorked), wd.weight) : 0;
                                return (
                                    <td key={wn} className='print-td print-hours-cell'>
                                        {hours > 0 ? (
                                            <>
                                                {hours.toFixed(2)}
                                                {wd?.weight !== undefined && wd.weight !== 100 && (
                                                    <span className='print-weight-badge'> ({wd.weight}%)</span>
                                                )}
                                            </>
                                        ) : ''}
                                    </td>
                                );
                            })}
                            <td className='print-td print-total-cell'>
                                {getMechanicWeightedTotalForType(link, pt.id).toFixed(2)}
                            </td>
                        </tr>
                    ))}
                    <tr className='print-totals-row'>
                        <td className='print-td'></td>
                        {weekNums.map(wn => (
                            <td key={wn} className='print-td print-hours-total'>
                                {getTotalHoursForWeekAndType(wn, pt.id).toFixed(2)}
                            </td>
                        ))}
                        <td className={`print-td print-grand-total ${typeResult >= 0 ? 'positive' : 'negative'}`}>
                            {typeActual.toFixed(2)}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    const renderFlatTable = () => {
        const weekNumbers = getWeekNumbers(props.link);
        return (
            <table className='print-table'>
                <thead>
                    <tr>
                        <th className='print-th print-mechanic-col' style={{ textAlign: 'left', paddingLeft: '8px' }}>{t('mechanic')}</th>
                        {weekNumbers.map(wn => (
                            <th key={wn} className='print-th print-week-header-col'>{t('week')} {wn}</th>
                        ))}
                        <th className='print-th print-total-col'>{t('total')}</th>
                    </tr>
                </thead>
                <tbody>
                    {props.link.map((link, i) => (
                        <tr key={i}>
                            <td className='print-td print-mechanic-name-col'>
                                {(link as any).mechanic?.name || `${t('mechanic')} ${link.mechanicId}`}
                            </td>
                            {weekNumbers.map(wn => {
                                const wd = getWeekDataForWeekAndType(link, wn);
                                const hours = wd ? hourMultipliedByWeight(secondsToHours(wd.secondsWorked), wd.weight) : 0;
                                return (
                                    <td key={wn} className='print-td print-hours-cell'>
                                        {hours > 0 ? (
                                            <>
                                                {hours.toFixed(2)}
                                                {wd?.weight !== undefined && wd.weight !== 100 && (
                                                    <span className='print-weight-badge'> ({wd.weight}%)</span>
                                                )}
                                            </>
                                        ) : ''}
                                    </td>
                                );
                            })}
                            <td className='print-td print-total-cell'>
                                {getMechanicWeightedTotalForType(link).toFixed(2)}
                            </td>
                        </tr>
                    ))}
                    <tr className='print-totals-row'>
                        <td className='print-td'></td>
                        {weekNumbers.map(wn => (
                            <td key={wn} className='print-td print-hours-total'>
                                {getTotalHoursForWeekAndType(wn).toFixed(2)}
                            </td>
                        ))}
                        <td className={`print-td print-grand-total ${remaining >= 0 ? 'positive' : 'negative'}`}>
                            {workedHours.toFixed(2)}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    return (
        <div ref={ref} className='print-page'>
            <div className='print-header'>
                <h1 className='print-title'>{props.project.name}</h1>
                <div className={`print-materials-status ${props.project.materialsDelivered ? 'delivered' : 'not-delivered'}`}>
                    Materiaal: {props.project.materialsDelivered ? 'Geleverd' : 'Niet geleverd'}
                </div>
            </div>

            {hasTypes ? projectTypes.map(renderTypeTable) : renderFlatTable()}

            <div className='print-summary'>
                <div className='print-summary-row'>
                    <span className='print-summary-label'>{t('hoursWorked')}:</span>
                    <span className='print-summary-value'>{workedHours.toFixed(2)}</span>
                </div>
                {dhzHours > 0 && (
                    <div className='print-summary-row'>
                        <span className='print-summary-label' style={{ color: '#c62828' }}>
                            {t('dhzWork')}:
                        </span>
                        <span className='print-summary-value' style={{ color: '#c62828' }}>
                            +{dhzHours.toFixed(2)}
                        </span>
                    </div>
                )}
                <div className='print-summary-row'>
                    <span className='print-summary-label'>{t('hoursEstimated')}:</span>
                    <span className='print-summary-value'>{estimatedHours.toFixed(2)}</span>
                </div>
                <div className='print-summary-row print-summary-result'>
                    <span className='print-summary-label'>{t('result')}:</span>
                    <span className={`print-summary-value ${remaining >= 0 ? 'positive' : 'negative'}`}>
                        {remaining.toFixed(2)}
                    </span>
                </div>
                {meerwerkHours > 0 && (
                    <div className='print-summary-row' style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #ccc' }}>
                        <span className='print-summary-label' style={{ color: '#1976d2' }}>
                            {t('meerwerk')} (apart te factureren):
                        </span>
                        <span className='print-summary-value' style={{ color: '#1976d2' }}>
                            +{meerwerkHours.toFixed(2)}
                        </span>
                    </div>
                )}
            </div>

            {props.project.notes && props.project.notes.length > 0 && (
                <div className='print-notes'>
                    <div className='print-notes-label'>{t('notes')}</div>
                    {[...props.project.notes]
                        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
                        .map(note => (
                            <div key={note.id} className='print-notes-entry'>
                                <span className='print-notes-date'>
                                    {new Date(note.created).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </span>
                                <div className='print-notes-content'>{note.content}</div>
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    );
});

export default HourOverviewPage;
